import { useMemo, useState } from "react";
import { Input } from "@electro-planner/ui";
import { listSymbols, searchSymbols, type SymbolDefinition } from "@electro-planner/symbols";

export const SYMBOL_DRAG_MIME = "application/x-electro-planner-symbol-id";

type Props = {
  onSymbolPick?: (symbol: SymbolDefinition) => void;
};

// Breda kategorier för paletten. En symbols familyKey mappas till en kategori
// via CATEGORY_BY_FAMILY. Okända family:er hamnar i "Övrigt" så biblioteket
// kan växa utan att palette-grupperingen går sönder.
type CategoryId =
  | "protection"
  | "switching"
  | "machines"
  | "connection"
  | "measurement"
  | "other";

const CATEGORY_LABEL: Record<CategoryId, string> = {
  protection: "Skydd",
  switching: "Kopplingsdon",
  machines: "Apparater",
  connection: "Uttag & anslutning",
  measurement: "Mätning & övervakning",
  other: "Övrigt",
};

const CATEGORY_ORDER: CategoryId[] = [
  "protection",
  "switching",
  "machines",
  "connection",
  "measurement",
  "other",
];

const CATEGORY_BY_FAMILY: Record<string, CategoryId> = {
  circuit_breaker: "protection",
  fuse: "protection",
  rcd: "protection",
  motor_protector: "protection",
  contactor: "switching",
  relay: "switching",
  switch: "switching",
  disconnector: "switching",
  push_button: "switching",
  motor: "machines",
  generator: "machines",
  transformer: "machines",
  power_supply: "machines",
  socket: "connection",
  terminal: "connection",
  earth: "connection",
  meter: "measurement",
  indicator_light: "measurement",
  sensor: "measurement",
};

function categoryOf(symbol: SymbolDefinition): CategoryId {
  return CATEGORY_BY_FAMILY[symbol.familyKey] ?? "other";
}

export function SymbolPalette({ onSymbolPick }: Props) {
  const [query, setQuery] = useState("");
  const isFiltering = query.trim().length > 0;
  const symbols = useMemo(
    () => (isFiltering ? searchSymbols(query) : listSymbols()),
    [query, isFiltering],
  );

  const grouped = useMemo(() => {
    if (isFiltering) return null;
    const byCat = new Map<CategoryId, SymbolDefinition[]>();
    for (const s of symbols) {
      const cat = categoryOf(s);
      const list = byCat.get(cat);
      if (list) list.push(s);
      else byCat.set(cat, [s]);
    }
    return CATEGORY_ORDER.filter((c) => byCat.has(c)).map((c) => ({
      id: c,
      label: CATEGORY_LABEL[c],
      symbols: byCat.get(c)!,
    }));
  }, [symbols, isFiltering]);

  return (
    <aside className="flex h-full w-64 flex-col border-r border-[var(--color-border)] bg-[var(--color-surface)]">
      <div className="border-b border-[var(--color-border)] p-3">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">
          Symbolbibliotek
        </div>
        <Input
          placeholder="Sök symbol…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <div className="flex-1 overflow-y-auto">
        {grouped ? (
          grouped.map((group) => (
            <section key={group.id}>
              <header className="sticky top-0 z-10 border-b border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-muted)]">
                {group.label}
                <span className="ml-1 font-normal opacity-70">({group.symbols.length})</span>
              </header>
              <SymbolList symbols={group.symbols} onSymbolPick={onSymbolPick} />
            </section>
          ))
        ) : (
          <SymbolList symbols={symbols} onSymbolPick={onSymbolPick} />
        )}
        {isFiltering && symbols.length === 0 ? (
          <p className="px-3 py-4 text-center text-xs text-[var(--color-muted)]">
            Inga symboler matchar "{query}".
          </p>
        ) : null}
      </div>
    </aside>
  );
}

function SymbolList({
  symbols,
  onSymbolPick,
}: {
  symbols: readonly SymbolDefinition[];
  onSymbolPick?: (symbol: SymbolDefinition) => void;
}) {
  return (
    <ul>
      {symbols.map((s) => (
        <li key={s.id}>
          <button
            type="button"
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(SYMBOL_DRAG_MIME, s.id);
              e.dataTransfer.setData("text/plain", s.id);
              e.dataTransfer.effectAllowed = "copy";
            }}
            onClick={() => onSymbolPick?.(s)}
            aria-label={`Infoga ${s.label.sv} (${s.designationPrefix})`}
            className="flex w-full items-center gap-3 border-b border-[var(--color-border)] px-3 py-2 text-left hover:bg-[var(--color-surface-hover,#f5f5f5)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-accent)]"
          >
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded border border-[var(--color-border)] bg-white"
              aria-hidden="true"
            >
              <svg
                width={32}
                height={32}
                viewBox={s.viewBox}
                style={{ color: "var(--color-fg, #111827)" }}
                // biome-ignore lint/security/noDangerouslySetInnerHtml: kontrollerat SVG-library
                dangerouslySetInnerHTML={{ __html: s.svg }}
              />
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-medium">{s.label.sv}</span>
              <span className="truncate text-xs text-[var(--color-muted)]">
                {s.designationPrefix} · {s.familyKey}
              </span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
